<template>
  <v-row
    ref="top"
    no-gutters
    class="pb-5"
  >
    <h3
      class="ml-9 mt-7 "
    >
      {{ $t('components.config.ConfigCharts.label.title.addChart') }}
    </h3>
    <v-toolbar
      width="100%"
      class="mx-sm-5 my-5"
      :style="{backgroundColor: 'transparent !important'}"
      flat
    >
      <v-text-field
        v-model="searchCard"
        flat
        clearable
        hide-details
        :label="$t('components.config.ConfigCharts.label.searchCard')"
      />
      <v-select
        v-model="chartPerPage"
        flat
        hide-details
        :items="chartPerPageOption"
        :label="$t('components.config.ConfigCharts.label.chartPerPage')"
        class="ml-16"
      />
    </v-toolbar>

    <v-row no-gutters>
      <v-tabs
        v-model="dataTypeTabIndexModel"
        background-color="transparent"
        grow
        show-arrows
        class="px-5"
      >
        <v-tab
          v-for="(tab, index) in dataTypeOptions"
          :key="index"
          width="100px"
          class="px-5"
          :href="dataTypeOptions.value"
        >
          {{ tab.text }}
        </v-tab>
      </v-tabs>

      <v-tabs
        v-if="isProprietaryDataTypeTab"
        v-model="proprietaryDataTypeTabIndexModel"
        background-color="transparent"
        grow
        class="px-5 proprietary-data-type-tabs"
      >
        <v-tab
          v-for="(tab, index) in proprietaryDataTypeOptions"
          :key="index"
          class="px-5"
        >
          {{ tab.text }}
        </v-tab>
      </v-tabs>

      <v-tabs-items
        v-model="dataTypeTabIndexModel"
      >
        <v-tab-item
          v-for="(tab, index) in dataTypeOptions"
          :key="index"
          :value="dataTypeOptions.value"
          @scroll.stop
        >
          <v-row
            no-gutters
            justify="center"
            class="grid"
          >
            <v-col
              v-for="card in showCardsFiltered"
              :key="card.id"
              cols="12"
              sm="6"
            >
              <v-card
                :key="card.id"
                flat
              >
                <CardWithThumbnail
                  :card="card"
                  :is-saving="isSaving"
                  @openHiddenCardsModal="openHiddenCardsModal(card)"
                />
              </v-card>
            </v-col>
          </v-row>
          <v-col cols="12">
            <v-row
              no-gutters
              justify="center"
              @click="scrollToTop"
            >
              <v-pagination
                v-model="page"
                class="my-4"
                circle
                total-visible="10"
                :length="paginationSize"
              />
            </v-row>
          </v-col>

          <AddCardModal
            v-if="showAddCardModal"
            v-model="showAddCardModal"
            @addChart="addChart"
          />
        </v-tab-item>
      </v-tabs-items>
    </v-row>
  </v-row>
</template>

<script>
import cardsModel from '@/mixins/cards-model';
import CardWithThumbnail from '@/components/cards/models/CardWithThumbnail';
import { mapActions, mapMutations, mapState } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import { generateRandomId } from '@/utils/math';
import { DATA_TYPE, SUBDATA_TYPE, WIDTH, HEIGHT } from '@/store/modules/cards/constants';
import { MUTATE } from '@/store/modules/mutation_types';
import { StFeatureFlagFlags } from '@/plugins/rollout';
import { trackEventWithUserProperties } from '@/utils/mixpanel';
import WarRoomService from '@/services/WarRoomService';
import AddCardModal from '../AddCardModal.vue';
import { flagIsEnabled } from '@/utils/feature-flags';

export default {
  name: 'ConfigCharts',
  components: {
    CardWithThumbnail,
    AddCardModal,
  },
  mixins: [
    cardsModel,
  ],
  data() {
    return {
      searchCard: '',
      page: 1,
      chartPerPageOption: [2, 4, 8, 16],
      chartPerPage: 2,
      dataTypeTabIndexModel: 0,
      proprietaryDataTypeTabIndexModel: 0,
      isSaving: false,
      showAddCardModal: false,
      selectedCard: {},
    };
  },
  computed: {
    ...mapState('filter', [
      'accountId',
    ]),
    ...mapState('dashboard', [
      'isStaging',
    ]),
    isProprietaryDataTypeTab() {
      const selectedTab = this.dataTypeOptions[this.dataTypeTabIndexModel];
      return selectedTab?.value.includes(DATA_TYPE.SAC) || false;
    },
    proprietaryDataTypeOptions() {
      return [
        {
          text: this.$t('components.config.ConfigCharts.dataType.mp'),
          value: [DATA_TYPE.SAC, DATA_TYPE.PAGE],
        },
        {
          text: this.$t('components.config.ConfigCharts.dataType.sc'),
          value: [DATA_TYPE.SAC_STATUS],
        },
      ];
    },
    selectedDataTypeValues() {
      if (this.isProprietaryDataTypeTab) {
        return this.proprietaryDataTypeOptions[this.proprietaryDataTypeTabIndexModel].value;
      }

      return this.dataTypeOptions[this.dataTypeTabIndexModel]?.value || [];
    },
    cardByDataType() {
      return this.allowCards
        .filter(card => {
          return (this.selectedDataTypeValues.includes(card.subDataType) || this.selectedDataTypeValues
            .includes(card.dataType)) &&
            this.hideInLibrary(card.hideInLibrary);
        }
        );
    },
    showCards() {
      if (!this.searchCard) {
        return this.cardByDataType;
      }

      return this.cardByDataType.filter(card => this.cardMatchesSearch(card));
    },
    showCardsFiltered() {
      const startOffset = (this.page - 1) * this.chartPerPage;
      const endOffset = this.page * this.chartPerPage;
      return this.showCards.slice(startOffset, endOffset);
    },
    paginationSize() {
      return Math.ceil(this.showCards.length / this.chartPerPage);
    },
    dataTypeOptions() {
      const shouldSkip = [DATA_TYPE.PAGE, DATA_TYPE.COMPETITIVE, DATA_TYPE.SAC_STATUS, DATA_TYPE.BLIP, DATA_TYPE.SAC_LISTENING, DATA_TYPE.CUSTOM];
      const exception = {
        [DATA_TYPE.LISTENING]: [DATA_TYPE.LISTENING, DATA_TYPE.COMPETITIVE],
        [DATA_TYPE.SAC]: [DATA_TYPE.SAC, DATA_TYPE.PAGE, DATA_TYPE.SAC_STATUS],
      };

      if (StFeatureFlagFlags.hideStilingueGalleryAndFilters.isEnabled()) {
        const filteredDataTabs = Object.values(DATA_TYPE).filter(dataType => dataType !== DATA_TYPE.BLIP && dataType !== DATA_TYPE.STATIC);

        shouldSkip.push(...filteredDataTabs);
      }

      if (StFeatureFlagFlags.showSacListeningComponents.isEnabled()) {
        exception[DATA_TYPE.SAC_LISTENING] = [DATA_TYPE.SAC_LISTENING];
        shouldSkip.splice(shouldSkip.indexOf(DATA_TYPE.SAC_LISTENING), 1);
      }

      if (StFeatureFlagFlags.showCustomComponents.isEnabled()) {
        exception[DATA_TYPE.CUSTOM] = [DATA_TYPE.CUSTOM];
        shouldSkip.splice(shouldSkip.indexOf(DATA_TYPE.CUSTOM), 1);
      }

      if (!this.activeDashboard.data.pages?.length) {
        shouldSkip.push(DATA_TYPE.SAC);
      }

      let includedTypesWithSubtypes = [];
      if (StFeatureFlagFlags.showBlipComponents.isEnabled()) {
        exception[DATA_TYPE.BLIP] = [DATA_TYPE.BLIP];
        shouldSkip.splice(shouldSkip.indexOf(DATA_TYPE.BLIP), 1);

        includedTypesWithSubtypes = Object.keys(SUBDATA_TYPE)
          .filter(dataType => !shouldSkip.includes(dataType));
      }

      const dataTabs = Object.values(DATA_TYPE)
        .filter(dataType => !shouldSkip.includes(dataType) &&
        !includedTypesWithSubtypes.map(dataType => dataType.toLowerCase()).includes(dataType));

      const subDataTabs = includedTypesWithSubtypes
        .reduce((acc, dataType) => acc.concat(Object.values(SUBDATA_TYPE[dataType])), []);

      dataTabs.push(...subDataTabs);

      return dataTabs
        .sort((_, y) => y === DATA_TYPE.STATIC ? -1 : 0)
        .map((dataType) => ({
          text: this.$t(`components.config.ConfigCharts.dataType.${dataType}`),
          value: exception[dataType] || [dataType],
        }));
    },
    cardWidth() {
      return WIDTH.MID;
    },
    cardHeight() {
      return HEIGHT.MID;
    },
  },
  watch: {
    searchCard() {
      this.page = 1;
    },
    chartPerPage() {
      this.page = 1;
    },
    dataTypeTabIndexModel() {
      this.page = 1;
    },
    proprietaryDataTypeTabIndexModel() {
      this.page = 1;
    },
  },
  methods: {
    ...mapActions('filter', [
      'applyGlobalFilter',
    ]),
    ...mapMutations('misc', {
      mutateMisc: MUTATE,
    }),
    hideInLibrary(flags = []) {
      if (!Boolean(flags.length)) return true;

      const mapFlags = flags.map(flag => {
        const featureFlagIsEnabled = flagIsEnabled(flag);
        return featureFlagIsEnabled();
      });
      return mapFlags.some(flag => !flag);
    },
    async openHiddenCardsModal(card) {
      this.selectedCard = { ...card };
      const shareManagement = await StFeatureFlagFlags.shareManagement.isEnabled();

      if (shareManagement && this.activeDashboard.data.isPublished && this.selectedCard.shareable === false) {
        this.showAddCardModal = true;

        this.mutateMisc({
          property: 'addCardDrawer',
          with: false,
        });

        return;
      }

      this.addChart();
    },
    async addChart() {
      this.isSaving = true;

      if (!this.activeDashboard.data.cards) {
        this.activeDashboard.data.cards = [];
      }

      const duplicatedCard = cloneDeep(this.selectedCard);
      const originalLibraryId = duplicatedCard.id;
      duplicatedCard.id = generateRandomId();

      const titleKey = `library.${originalLibraryId}.title`;
      if (this.$te(titleKey)) {
        duplicatedCard.props.title = this.$t(titleKey);
      }

      delete duplicatedCard.hideInLibrary;

      const newCardFilter = await this.applyGlobalFilter({ card: duplicatedCard });

      duplicatedCard.props.filterId = await WarRoomService.encodeFilter({
        account_id: this.accountId,
        is_staging: this.isStaging,
        filter: JSON.stringify(newCardFilter),
      });

      this.activeDashboard.data.cards.push(duplicatedCard);

      this.saveCards().then(() => {
        this.isSaving = false;
      });

      trackEventWithUserProperties('ST CardGallery Card Add', { Widget: this.selectedCard.props.title });
    },
    cardMatchesSearch({ id, props: cardProps }) {
      const { title, description, tags } = cardProps;
      const candidates = [
        title,
        description,
        tags,
        this.$te(`library.${id}.title`) ? this.$t(`library.${id}.title`) : null,
        this.$te(`library.${id}.description`) ? this.$t(`library.${id}.description`) : null,
      ];
      return candidates.some(c => this.isIncludedInCardProperties(c));
    },
    isIncludedInCardProperties(property) {
      return property ? property.toLowerCase().includes(this.searchCard.toLowerCase()) : false;
    },
    scrollToTop(event) {
      const clickedElementClasses = [...event.target.classList];
      const clickableElementsClasses = ['v-pagination__item', 'v-icon'];
      if (clickedElementClasses.some(c => clickableElementsClasses.includes(c))) {
        this.$refs.top.scrollIntoView({ behavior: 'smooth' });
      }
    },
  },
};
</script>

<style scoped lang="scss">
  .theme--dark h3 {
    color: white;
  }

  .allow-chart-item {
    pointer-events: none;
    opacity: 0.8;
    cursor: default;
  }

  .v-tab {
    text-transform: none;
    letter-spacing: 0;
    padding: 0;
    font-weight: var(--ds-font-weight-semibold);
    font-size: var(--ds-font-size-sm);
    line-height: var(--ds-font-line-height-tight);
  }

  .v-tabs {
    width: 100px !important;
  }

  .v-tabs-items {
    width: 100%;
  }

  .proprietary-data-type-tabs {
    width: 100% !important;
  }
</style>
